import React, { useCallback, useState } from 'react';
import { Button, Form, Modal, ModalProps } from 'react-bootstrap';
import { LanguageOption } from '@/lib/models';
import { documentService } from '@/lib/services';
import { createUseThemedStyles, useHandleError } from '@/hooks';
import { Select } from '@/components';

const useStyles = createUseThemedStyles({
	dialog: {
		width: '90%',
		height: '100%',
		maxWidth: 610,
		margin: '0 auto',
		'& .modal-content': {
			maxHeight: '90vh',
		},
		'& .hackett-modal__body': {
			overflowY: 'auto',
		},
	},
});

interface AiSummaryLanguageModalProps extends ModalProps {
	title: string;
	inputLabel: string;
	submitButtonTitle: string;
	onSubmit(languageSelection: string): void;
}

export const AiSummaryLanguageModal = ({
	title,
	inputLabel,
	submitButtonTitle,
	onSubmit,
	...props
}: AiSummaryLanguageModalProps) => {
	const classes = useStyles();
	const handleError = useHandleError();

	const [isLoading, setIsLoading] = useState(false);
	const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>([]);
	const [languageSelection, setLanguageSelection] = useState('');

	const handleOnEnter = useCallback(async () => {
		setIsLoading(true);

		try {
			const response = await documentService.getDocumentLanguages().fetch();
			const browserLanguage =
				typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
			const browserLanguageIso = browserLanguage.split('-')[0];

			setLanguageOptions(response);
			setLanguageSelection(browserLanguageIso);
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [handleError]);

	const handleOnExit = useCallback(() => {
		setLanguageOptions([]);
		setLanguageSelection('');
	}, []);

	function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		onSubmit(languageSelection);
	}

	return (
		<Modal dialogClassName={classes.dialog} centered onEntering={handleOnEnter} onExited={handleOnExit} {...props}>
			<Modal.Header closeButton>
				<Modal.Title>{title}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form onSubmit={handleFormSubmit}>
					<fieldset disabled={isLoading}>
						<Form.Group className="mb-8">
							<Form.Label>{inputLabel}</Form.Label>
							<Select
								value={languageSelection}
								onChange={({ currentTarget }) => {
									setLanguageSelection(currentTarget.value);
								}}
							>
								{languageOptions.map((i) => (
									<option key={i.code} value={i.code}>
										{i.name}
									</option>
								))}
							</Select>
						</Form.Group>
						<div className="text-right">
							<Button type="button" className="mr-2" variant="outline-primary" onClick={props.onHide}>
								Close
							</Button>
							<Button type="submit" variant="primary">
								{submitButtonTitle}
							</Button>
						</div>
					</fieldset>
				</Form>
			</Modal.Body>
		</Modal>
	);
};
